import { getRegistryDocument } from '@/api/backend/registry';
import { RegistryType, RegistryDocumentType } from '@/api/backend/types';
import { DownloadFilled } from '@/assets/icons';
import { FormErrorAlert, FormErrorAlertProps } from '@/components/form-error-alert';
import { ResponsiveDataView } from '@/components/responsive-data-view';
import { downloadFile } from '@/lib/utils';
import {
  DataTableColumnType,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@opaz/ui-components';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const ServiceProviderDocumentsTab = ({
  data,
}: {
  data: RegistryType & { documents?: RegistryDocumentType[] };
}) => {
  const { t } = useTranslation();
  const [error, setError] = useState<FormErrorAlertProps['error']>(null);

  const { mutateAsync: downloadDocument, isPending } = useMutation({
    mutationFn: async (document: RegistryDocumentType) => {
      console.debug(`Downloading document with ID: ${document.documentNumber}`);
      const blob = await getRegistryDocument(data.registryNumber, document.documentNumber);
      return {
        blob,
        documentName: document.name,
      };
    },
    onSuccess: ({ blob, documentName }) => {
      downloadFile(blob, documentName);
    },
    onError: (error: AxiosError) => {
      console.error('Error downloading document:', error);
      setError(error);
    },
  });

  const columns: DataTableColumnType<RegistryDocumentType>[] = [
    {
      title: t('registry.contract.sectionTabs.documentsTab.table.headings.documentName'),
      render: (document) => document.name,
    },
    {
      title: t('common.download'),
      render: (document) => (
        <Link disabled={isPending} className="flex gap-2 items-center" onClick={() => downloadDocument(document)}>
          <DownloadFilled /> {t('common.download')}
        </Link>
      ),
    },
  ];

  return (
    <>
      <FormErrorAlert error={error} />
      <h2 className="text-foreground text-2xl font-bold mb-4">
        {t('registry.contract.sectionTabs.documentsTab.title')}
      </h2>

      <ResponsiveDataView
        columns={columns}
        data={data.documents ?? []}
        emptyMessage={t('common.table.emptyState')}
        getRowKey={(document) => document.documentNumber}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[300px]">
                {t('registry.contract.sectionTabs.documentsTab.table.headings.documentName')}
              </TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.documents && data.documents.length > 0 ? (
              data.documents.map((document) => (
                <TableRow key={document.documentNumber}>
                  <TableCell>{document.name}</TableCell>
                  <TableCell>
                    <Link
                      disabled={isPending}
                      className="flex gap-2 items-center"
                      onClick={() => downloadDocument(document)}
                    >
                      <DownloadFilled /> {t('common.download')}
                    </Link>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2} className="text-center">
                  {t('common.table.emptyState')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ResponsiveDataView>
    </>
  );
};
